import { apiFetch, httpError } from '../services/apiFetch';

const API_URL = import.meta.env.VITE_API_URL;

// Accounting integration (Xero / QuickBooks / Sage). The backend owns OAuth +
// token storage + the sync engine; the web app only initiates a connection and
// reads status. provider is 'xero' | 'quickbooks' | 'sage'.

export async function fetchConnections() {
    const res = await apiFetch(`${API_URL}/api/Accounting/Connections`);
    if (!res.ok) throw await httpError(res, 'Failed to load accounting connections');
    return res.json();
}

// Returns { authUrl } for the provider's hosted login/consent page. returnTo is
// where the backend bounces the browser back after the OAuth round-trip.
export async function startConnect(provider, returnTo) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Connect/${provider}`, {
        method: 'POST',
        body: JSON.stringify({ returnTo }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to start connection');
    return res.json();
}

export async function disconnect(provider) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Disconnect/${provider}`, {
        method: 'POST',
    });
    if (!res.ok) throw await httpError(res, 'Failed to disconnect');
}

// Company/org picker for the wizard.
export async function fetchTenants(provider) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Tenants/${provider}`);
    if (!res.ok) throw await httpError(res, 'Failed to load companies');
    return res.json();
}

export async function selectTenant(provider, tenantId) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Tenants/${provider}/Select`, {
        method: 'POST',
        body: JSON.stringify({ tenantId }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to select company');
    return res.json();
}

export async function fetchSettings() {
    const res = await apiFetch(`${API_URL}/api/Accounting/Settings`);
    if (res.status === 404) return null; // not connected yet
    if (!res.ok) throw await httpError(res, 'Failed to load accounting settings');
    return res.json();
}

// body: { autoSync, defaultSalesAccountCode, defaultPurchaseAccountCode, defaultTaxType, defaultSalesItem, contactMatchStrategy }
export async function updateSettings(body) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Settings`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to save accounting settings');
    return res.json();
}

export async function syncInvoice(id) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Sync/Invoice/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to sync invoice');
    return res.json();
}

export async function syncExpense(id) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Sync/Expense/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to sync expense');
    return res.json();
}

export async function syncCustomer(id) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Sync/Customer/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to sync customer');
    return res.json();
}

// Bulk-push every customer (initial onboarding). Returns { queued }.
export async function syncAllCustomers() {
    const res = await apiFetch(`${API_URL}/api/Accounting/Sync/Customers`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to start customer sync');
    return res.json();
}

// Per-record status. type: 'Invoice' | 'Expense' | 'Customer'.
export async function fetchRecordStatus(type, id) {
    const res = await apiFetch(`${API_URL}/api/Accounting/Status/${type}/${id}`);
    if (!res.ok) throw await httpError(res, 'Failed to load sync status');
    return res.json();
}
